import { useEffect, useState } from "react";
import { Link } from "react-router";
import { motion, useReducedMotion } from "framer-motion";
import { hashHref } from "../../constants/site";
import Images from "../../utils/Images";
import { HERO_MEDIA } from "../../constants/about";
import { CornerMarks, EASE, useKnockoutLogo } from "./ui";

const NODES = [
  {
    id: "market",
    name: "SimMarkets",
    line: "Marketplace",
    href: hashHref("simmarket"),
    logo: Images.simmarket_colored,
    x: "50%",
    y: "14%",
  },
  {
    id: "verify",
    name: "SimVerify",
    line: "Digital trust",
    href: "/simverify",
    logo: Images.simverify_logo,
    x: "18%",
    y: "72%",
  },
  {
    id: "collect",
    name: "SimCollect",
    line: "Collections",
    href: "/simcollect",
    logo: Images.simcollect_logo,
    x: "82%",
    y: "72%",
  },
];

function AboutHero() {
  const reduce = useReducedMotion();
  const [active, setActive] = useState(0);
  const enter = reduce ? { duration: 0 } : { duration: 0.55, ease: EASE };
  const fadeUp = {
    hidden: reduce ? { opacity: 1, y: 0 } : { opacity: 0, y: 22 },
    show: { opacity: 1, y: 0, transition: enter },
  };

  useEffect(() => {
    if (reduce) return undefined;
    const id = window.setInterval(() => {
      setActive((step) => (step + 1) % NODES.length);
    }, 2800);
    return () => window.clearInterval(id);
  }, [reduce]);

  return (
    <section
      id="about-hero"
      className="relative overflow-hidden bg-white text-navy"
      aria-labelledby="about-page-heading"
    >
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute inset-0 bg-[radial-gradient(90%_70%_at_100%_0%,rgba(255,191,24,0.16),transparent_55%),radial-gradient(80%_60%_at_0%_100%,rgba(16,14,52,0.05),transparent_50%),linear-gradient(180deg,#ffffff_0%,#fffdf6_48%,#ffffff_100%)]" />
        <div
          className="absolute inset-0 opacity-[0.42]"
          style={{
            backgroundImage: "radial-gradient(rgba(16,14,52,0.09) 1px, transparent 1px)",
            backgroundSize: "26px 26px",
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.16]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(16,14,52,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(16,14,52,0.06) 1px, transparent 1px)",
            backgroundSize: "88px 88px",
          }}
        />
        <div className="absolute -left-24 top-28 h-[28rem] w-[28rem] rounded-full border border-navy/[0.06]" />
        <div className="absolute -left-8 top-44 h-[18rem] w-[18rem] rounded-full border border-gold/20" />
        <div className="absolute -right-16 bottom-10 h-[22rem] w-[22rem] rounded-full border border-navy/[0.05]" />
        <p className="absolute -left-6 top-8 select-none whitespace-nowrap text-[9rem] font-black leading-none tracking-tight text-navy/[0.035] sm:text-[13rem] lg:top-2 lg:text-[16rem]">
          ABOUT
        </p>
      </div>
      <div className="pointer-events-none absolute inset-x-0 top-0 z-[3] h-px bg-gradient-to-r from-transparent via-gold/70 to-transparent" />

      <div className="relative mx-auto grid max-w-8xl items-stretch gap-10 px-4 pb-12 pt-[5.5rem] sm:px-6 lg:grid-cols-[minmax(0,1.12fr)_minmax(0,0.88fr)] lg:gap-8 lg:px-8 lg:pb-16 lg:pt-[6rem]">
        <motion.div
          className="relative z-[3] flex min-w-0 flex-col pt-6 [container-type:inline-size] lg:pt-8"
          initial="hidden"
          animate="show"
          variants={{
            hidden: {},
            show: {
              transition: {
                staggerChildren: reduce ? 0 : 0.09,
                delayChildren: reduce ? 0 : 0.06,
              },
            },
          }}
        >
          <motion.p
            className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40"
            variants={fadeUp}
          >
            <Link
              to="/"
              className="transition hover:text-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold"
            >
              Home
            </Link>
            <span className="mx-2 text-navy/20">/</span>
            About Us
          </motion.p>

          <motion.h1
            id="about-page-heading"
            className="mt-5 font-semibold leading-[1.08] tracking-tight text-navy [font-size:clamp(2.05rem,7.2cqi,4.15rem)]"
            variants={fadeUp}
          >
            <span className="block">Building Africa&apos;s intelligent</span>
            <span className="block">digital financial ecosystem</span>
          </motion.h1>

          <motion.p
            className="mt-6 max-w-2xl font-medium leading-snug text-navy/70 [font-size:clamp(1.05rem,3.2cqi,1.35rem)]"
            variants={fadeUp}
          >
            One Ecosystem. Three Intelligent Solutions. Endless Opportunities.
          </motion.p>

          <motion.div variants={fadeUp} className="mt-7 max-w-[40rem]">
            <p className="leading-[1.8] text-navy/75 [font-size:clamp(1rem,2.7cqi,1.125rem)]">
              At SimFinance, we are redefining how consumers and financial institutions connect, interact
              and grow together. We have built an intelligent digital ecosystem that combines financial
              education, AI-powered product discovery, digital trust verification and intelligent debt
              recovery into one integrated platform.
            </p>
            <p className="mt-4 leading-[1.8] text-navy/65 [font-size:clamp(0.98rem,2.5cqi,1.0625rem)]">
              Whether you&apos;re a consumer searching for the right financial product, a financial
              institution looking to acquire quality customers, or a partner seeking innovative digital
              solutions, SimFinance provides the technology, intelligence and trusted ecosystem to help
              you succeed.
            </p>
            <p className="mt-4 leading-[1.8] text-navy/65 [font-size:clamp(0.98rem,2.5cqi,1.0625rem)]">
              Through our flagship solutions—SimMarkets, SimVerify and SimCollect—we are transforming the
              complete financial customer journey, from awareness and education to lending, verification,
              repayment and long-term customer engagement.
            </p>
          </motion.div>

          <motion.blockquote
            className="mt-8 max-w-xl border-l-2 border-gold pl-5"
            variants={fadeUp}
          >
            <p className="text-base font-semibold leading-snug tracking-tight text-navy sm:text-lg">
              Smarter decisions. Trusted connections. Sustainable financial growth.
            </p>
          </motion.blockquote>

          <motion.div className="mt-10 flex flex-wrap items-center gap-3" variants={fadeUp}>
            <a
              href="#ecosystem"
              className="inline-flex min-h-12 cursor-pointer items-center rounded-full bg-gold px-7 text-base font-semibold text-navy transition hover:bg-[#ffd056] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2"
            >
              Explore the ecosystem
            </a>
            <a
              href={hashHref("contact")}
              className="inline-flex min-h-12 cursor-pointer items-center rounded-full border border-navy/15 px-7 text-base font-semibold text-navy transition hover:border-navy hover:bg-navy hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy focus-visible:ring-offset-2"
            >
              Talk to an advisor
            </a>
          </motion.div>
        </motion.div>

        <motion.div
          className="relative z-[2] min-w-0 lg:pl-4"
          initial={reduce ? false : { opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...enter, delay: reduce ? 0 : 0.12 }}
        >
          <div className="relative h-full min-h-[28rem] lg:min-h-full">
            <CornerMarks />
            <div className="relative flex h-full min-h-[28rem] flex-col overflow-hidden border border-navy/10 bg-navy lg:min-h-[40rem]">
              <HeroBackdrop src={HERO_MEDIA.src} alt={HERO_MEDIA.alt} />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-navy via-navy/70 to-navy/40" />
              <EcosystemStage active={active} reduce={reduce} onSelect={setActive} />
            </div>
          </div>
        </motion.div>
      </div>

      <div className="relative z-[3] border-t border-navy/10 bg-white/70 backdrop-blur-[2px]">
        <span className="absolute left-1/2 top-0 inline-flex -translate-x-1/2 -translate-y-1/2 items-center gap-2 whitespace-nowrap rounded-full border border-navy/10 bg-white px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-navy/45 shadow-sm">
          <span className="h-1.5 w-1.5 rounded-full bg-gold" />
          Every decision rests on
        </span>
        <ul className="mx-auto grid max-w-8xl sm:grid-cols-3" aria-label="Foundations of every financial decision">
          {[
            { n: "01", title: "Knowledge", line: "Understand before you choose" },
            { n: "02", title: "Trust", line: "Verify before you transact" },
            { n: "03", title: "Confidence", line: "Act with intelligence" },
          ].map((item, index) => (
            <li
              key={item.title}
              className={`flex min-h-[5.5rem] items-center justify-between px-4 py-5 sm:px-8 ${
                index > 0 ? "sm:border-l sm:border-navy/10" : ""
              }`}
            >
              <span>
                <span className="block font-mono text-[11px] font-semibold tracking-[0.2em] text-gold">
                  {item.n}
                </span>
                <span className="mt-1 block text-sm font-semibold text-navy">{item.title}</span>
                <span className="mt-0.5 block text-sm text-navy/50">{item.line}</span>
              </span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function HeroBackdrop({ src, alt }) {
  const [failed, setFailed] = useState(!src);

  if (failed) return null;

  return (
    <img
      src={src}
      alt={alt}
      className="absolute inset-0 h-full w-full object-cover"
      onError={() => setFailed(true)}
    />
  );
}

function EcosystemStage({ active, reduce, onSelect }) {
  return (
    <div className="relative z-[1] flex h-full min-h-0 flex-1 flex-col justify-between p-5 sm:p-6 lg:p-8">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-gold/90">One platform</p>
          <p className="mt-2 max-w-[14rem] text-lg font-semibold leading-snug tracking-tight text-white">
            Three intelligent solutions. One journey.
          </p>
        </div>
        <p className="hidden text-right text-[11px] font-semibold uppercase tracking-[0.2em] text-white/40 sm:block">
          {String(active + 1).padStart(2, "0")} / 03
        </p>
      </div>

      <div className="relative mx-auto my-6 aspect-square w-full max-w-[22rem]">
        <span className="absolute inset-[12%] rounded-full border border-white/10" aria-hidden="true" />
        <span className="absolute inset-[28%] rounded-full border border-gold/30" aria-hidden="true" />
        {!reduce ? (
          <motion.span
            className="absolute inset-[12%] rounded-full border border-gold/20"
            animate={{ rotate: 360 }}
            transition={{ duration: 28, repeat: Infinity, ease: "linear" }}
            aria-hidden="true"
          />
        ) : null}

        <div className="absolute left-1/2 top-1/2 z-[2] flex h-[4.35rem] w-[10.5rem] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white px-3 shadow-[0_18px_40px_rgba(0,0,0,0.28)]">
          <BrandMark src={Images.logo} alt="SimFinance" className="h-8 w-auto max-w-[9rem]" />
        </div>

        {NODES.map((node, index) => (
          <PortalNode
            key={node.id}
            node={node}
            isActive={index === active}
            onSelect={() => onSelect(index)}
          />
        ))}
      </div>

      <div>
        <p className="text-sm leading-relaxed text-white/60">
          Hover or select a node to preview. Each solution is built to hand off to the next—so acquisition,
          trust and recovery stay connected.
        </p>
        <p className="mt-3 text-[11px] leading-relaxed text-white/35">
          Illustration slot · 1600×2000 · /images/about-page/hero.png · West African professionals, navy/gold
          lighting, no text in frame.
        </p>
      </div>
    </div>
  );
}

function PortalNode({ node, isActive, onSelect }) {
  return (
    <a
      href={node.href}
      className="absolute z-[3] -translate-x-1/2 -translate-y-1/2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold"
      style={{ left: node.x, top: node.y }}
      onMouseEnter={onSelect}
      onFocus={onSelect}
      aria-label={`${node.name} — ${node.line}`}
    >
      <span
        className={`flex h-[3.35rem] w-[10.75rem] cursor-pointer items-center justify-center overflow-hidden rounded-2xl border bg-white px-2.5 transition ${
          isActive
            ? "border-gold shadow-[0_12px_28px_rgba(255,191,24,0.35)]"
            : "border-white/20 hover:border-gold/70"
        }`}
      >
        <BrandMark src={node.logo} alt={node.name} className="h-8 w-auto max-w-[9.5rem]" />
      </span>
    </a>
  );
}

function BrandMark({ src, alt, className }) {
  const { href, failed } = useKnockoutLogo(src);
  const display = !failed && href ? href : src;

  if (!display) {
    return alt ? <span className="text-xs font-semibold tracking-tight text-navy">{alt}</span> : null;
  }

  return <img src={display} alt={alt} className={`object-contain object-center ${className}`} />;
}

export default AboutHero;
